import { Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormLabel, Grid, IconButton, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { Fragment, useEffect, useRef, useState } from "react";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Replay from '@mui/icons-material/Replay';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InventoryService from "src/_cores/_services/inventory.service";
import { ICategory } from "src/_cores/_interfaces/inventory.interface";
import UploadInput from "src/_shares/_components/input/upload-input";
import { Editor } from "@tinymce/tinymce-react";
import { GlobalConfig } from "src/_configs/global.config";
import { api } from "src/_cores/_api/api";
import { INVENTORY_API_URL, SALES_API_URL } from "src/_cores/_enums/api-url.enum";
import { DateTimeHelper } from "src/_shares/_helpers/datetime-helper";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const BrandList = () => {
    const editorRef = useRef<any>(null);
    const [open, setOpen] = useState(false);
    const uploadRef = useRef<any>();

    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState<ICategory[]>([]);
    const [formData, setFormData] = useState({
        id: -1,
        name: '',
        categoryIds: [] as any[],
        imagePath: '',
        description: '',
        descriptionTitle: '',
        isHighlight: false,
    });

    const [shops, setShops] = useState<any[]>([]);
    const [shopDetail, setShopDetail] = useState<any>(null);
    const [openShopDetail, setOpenShopDetail] = useState(false);

    const [shopFilter, setShopFilter] = useState({ name: '', status: '1' });
    const [brandFilter, setBrandFilter] = useState({ name: '', categoryIds: [] as number[], status: 'true' });

    useEffect(() => {
        async function getCategories() {
            const cats = await InventoryService.getCategories() as any;
            setCategories(cats?.data);
        }
        getCategories();
        getShops();
        getData();
    }, []);

    const getShops = async () => {
        const res = await api.get(SALES_API_URL.MY_SHOPS) as any;
        if (res?.isSucceed) {
            setShops(res.data || []);
        }
    }

    const viewShopDetail = async (id: number) => {
        const res = await api.get(SALES_API_URL.GET_SHOP + `/${id}`) as any;
        if (res) {
            setShopDetail(res);
            setOpenShopDetail(true);
        }
    }

    const handleClose = () => {
        setFormData({
            ...formData,
            id: -1,
            name: '',
            categoryIds: [] as any[],
            imagePath: '',
            description: '',
            descriptionTitle: '',
        });
        setOpen(false);
    };

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const onChangeFiles = (files: string[]) => {
        setFormData({ ...formData, imagePath: files[0] ?? '' });
    }

    const onChangeCategories = (categories: ICategory[]) => {
        const _ids = categories.map(_ => _.categoryId);
        setFormData({ ...formData, categoryIds: _ids });
    }

    const handleClickOpen = () => {
        setOpen(true);
    };

    const viewDetail = async (id: number) => {
        const res = await InventoryService.getBrand(id) as any;
        if (res?.isSucceed) {
            const cats = await InventoryService.getCategories() as any;
            const dataDetail = res?.data as any;
            if (cats?.isSucceed) {
                setFormData({
                    id: dataDetail.id,
                    name: dataDetail.name,
                    categoryIds: dataDetail.categories.map((_: ICategory) => _.categoryId),
                    imagePath: dataDetail.imagePath,
                    descriptionTitle: dataDetail.descriptionTitle,
                    description: dataDetail.description,
                    isHighlight: dataDetail.isHighlights,
                });
                setCategories(cats?.data);
                handleClickOpen();
            }
        }
    }

    const onChangeIsHighlight = (event: any) => {
        setFormData({ ...formData, isHighlight: event.target.checked });
    }

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        const params = {
            ...formData,
            description: editorRef.current.getContent(),
            isHighlight: formData['isHighlight'] || false
        }
        const url = formData.id > -1
            ? INVENTORY_API_URL.UPDATE_BRAND
            : INVENTORY_API_URL.ADD_BRAND;
        const res = await api.post(url, params) as any
        if (res?.isSucceed)
            await getData();
        handleClose();
    };

    const getData = async () => {
        const res = await InventoryService.getBrands({
            isMyShop: true,
            showAll: true,
        }) as any;
        if (res?.isSucceed) {
            setBrands(res.data);
        }
    }

    const updateStatus = async (id: number, newStatus: boolean) => {
        const res = await InventoryService.updateBrandStatus({ id, status: newStatus }) as any;
        if (res?.isSucceed) {
            await getData();
        }
    }

    const closeDialogAndRemoveFiles = () => {
        handleClose();
        if (uploadRef.current) {
            //uploadRef.current.removeAllFiles();
        }
    }

    const filteredShops = shops.filter(s => {
        const matchName = !shopFilter.name || s.shopName?.toLowerCase().includes(shopFilter.name.toLowerCase());
        const matchStatus = shopFilter.status === '' || String(s.status) === shopFilter.status;
        return matchName && matchStatus;
    });

    const filteredBrands = (brands as any[]).filter(b => {
        const matchName = !brandFilter.name || b.name?.toLowerCase().includes(brandFilter.name.toLowerCase());
        const matchCategory = brandFilter.categoryIds.length === 0 ||
            brandFilter.categoryIds.some(id => (b.categories || []).some((c: any) => c.categoryId === id));
        const matchStatus = brandFilter.status === '' ||
            (brandFilter.status === 'true' && b.status) ||
            (brandFilter.status === 'false' && !b.status);
        return matchName && matchCategory && matchStatus;
    });

    return (
        <Fragment>
            {!open && (<>
                {/* Shop table */}
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Cửa hàng</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        label="Tìm theo tên"
                        value={shopFilter.name}
                        onChange={e => setShopFilter(f => ({ ...f, name: e.target.value }))}
                        sx={{ minWidth: 200 }}
                    />
                    <TextField
                        select size="small" label="Trạng thái"
                        value={shopFilter.status}
                        onChange={e => setShopFilter(f => ({ ...f, status: e.target.value }))}
                        sx={{ minWidth: 140 }}
                    >
                        <MenuItem value="">Tất cả</MenuItem>
                        <MenuItem value="1">Hoạt động</MenuItem>
                        <MenuItem value="0">Đã khóa</MenuItem>
                    </TextField>
                </Box>
                <TableContainer component={Paper} sx={{ marginBottom: 3 }}>
                    <Table size="small" aria-label="shop table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Ngày tham gia</TableCell>
                                <TableCell>Tên cửa hàng</TableCell>
                                <TableCell>Người liên hệ</TableCell>
                                <TableCell>Người dùng liên kết</TableCell>
                                <TableCell>Trạng thái</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredShops.map((shop: any) => (
                                <TableRow key={shop.shopId} sx={{ '& > *': { borderBottom: 'unset' } }}>
                                    <TableCell>{DateTimeHelper.getDateTimeFormated(shop.shopJoinDate)}</TableCell>
                                    <TableCell>{shop.shopName}</TableCell>
                                    <TableCell>{shop.userFullName}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {(shop.shopUsers || []).map((u: any) => (
                                                <Chip key={u.id} label={u.name} size="small" variant="outlined" />
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={shop.status === 0 ? 'Đã khóa' : 'Hoạt động'}
                                            color={shop.status === 0 ? 'default' : 'success'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton size="small" onClick={() => viewShopDetail(shop.shopId)}>
                                            <VisibilityIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Brand table */}
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Thương hiệu</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Button variant="contained" onClick={handleClickOpen}>Thêm</Button>
                    <TextField
                        size="small"
                        label="Tìm theo tên"
                        value={brandFilter.name}
                        onChange={e => setBrandFilter(f => ({ ...f, name: e.target.value }))}
                        sx={{ minWidth: 200 }}
                    />
                    <Autocomplete
                        multiple
                        size="small"
                        options={categories}
                        disableCloseOnSelect
                        getOptionLabel={(item: ICategory) => item.categoryName}
                        value={categories.filter(c => brandFilter.categoryIds.includes(c.categoryId))}
                        onChange={(_, items) => setBrandFilter(f => ({ ...f, categoryIds: items.map(i => i.categoryId) }))}
                        renderOption={(props, item, { selected }) => (
                            <li {...props}>
                                <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} size="small" />
                                {item.categoryName}
                            </li>
                        )}
                        renderInput={params => <TextField {...params} label="Danh mục" placeholder="Lọc danh mục" />}
                        sx={{ minWidth: 240 }}
                    />
                    <TextField
                        select size="small" label="Trạng thái"
                        value={brandFilter.status}
                        onChange={e => setBrandFilter(f => ({ ...f, status: e.target.value }))}
                        sx={{ minWidth: 140 }}
                    >
                        <MenuItem value="">Tất cả</MenuItem>
                        <MenuItem value="true">Hoạt động</MenuItem>
                        <MenuItem value="false">Vô hiệu</MenuItem>
                    </TextField>
                </Box>
                <TableContainer component={Paper} sx={{ marginBottom: 2 }}>
                    <Table aria-label="collapsible table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Ảnh</TableCell>
                                <TableCell>Thương hiệu</TableCell>
                                <TableCell>Danh mục</TableCell>
                                <TableCell>Trạng thái</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredBrands.map((_: any) => (
                                <TableRow key={_.id} sx={{ '& > *': { borderBottom: 'unset' } }}>
                                    <TableCell>
                                        <Box className="flex">
                                            <Box className="mr-2 items-center justify-between h-[100px] w-[100px]">
                                                <img
                                                    src={`${process.env.REACT_APP_IMAGE_URL}/brand/${_.imagePath}`}
                                                    alt={_.name}
                                                    loading="lazy"
                                                />
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{_.name}</TableCell>
                                    <TableCell>{_.categoryNames.join(', ')}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={_.status ? 'Hoạt động' : 'Vô hiệu'}
                                            color={_.status ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            size="small"
                                            onClick={() => viewDetail(_.id)}
                                        >
                                            <ModeEditIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => updateStatus(_.id, !_.status)}
                                        >
                                            {!_.status ? <Replay /> : <DeleteOutlineIcon />}
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </>)}

            {/* Shop detail dialog — read only */}
            <Dialog
                fullWidth
                open={openShopDetail}
                onClose={() => setOpenShopDetail(false)}
            >
                <DialogTitle>Chi tiết cửa hàng</DialogTitle>
                <DialogContent>
                    <TextField
                        margin="dense"
                        label="Tên cửa hàng"
                        fullWidth
                        variant="standard"
                        value={shopDetail?.shopName || ''}
                        InputProps={{ readOnly: true }}
                    />
                    <TextField
                        margin="dense"
                        label="Ngày tham gia"
                        fullWidth
                        variant="standard"
                        value={shopDetail?.shopJoinDate ? DateTimeHelper.getDateTimeFormated(shopDetail.shopJoinDate) : ''}
                        InputProps={{ readOnly: true }}
                    />
                    <TextField
                        margin="dense"
                        label="Người liên hệ"
                        fullWidth
                        variant="standard"
                        value={shopDetail?.userFullName || ''}
                        InputProps={{ readOnly: true }}
                    />
                    <TextField
                        margin="dense"
                        label="Chiết khấu (%)"
                        fullWidth
                        variant="standard"
                        value={shopDetail?.tax ?? ''}
                        InputProps={{ readOnly: true }}
                    />
                    {shopDetail?.shopBrands?.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">Thương hiệu</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {shopDetail.shopBrands.map((b: any) => (
                                    <Chip key={b.id} label={b.name} size="small" variant="outlined" />
                                ))}
                            </Box>
                        </Box>
                    )}
                    {shopDetail?.shopUsers?.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">Người dùng liên kết</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {shopDetail.shopUsers.map((u: any) => (
                                    <Chip key={u.id} label={u.name} size="small" variant="outlined" />
                                ))}
                            </Box>
                        </Box>
                    )}
                    <Box sx={{ mt: 1 }}>
                        <Chip
                            label={shopDetail?.status === 0 ? 'Đã khóa' : 'Hoạt động'}
                            color={shopDetail?.status === 0 ? 'default' : 'success'}
                            size="small"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenShopDetail(false)}>Đóng</Button>
                </DialogActions>
            </Dialog>

            {/* Brand form */}
            {open && (
                <Box component={'form'} onSubmit={handleSubmit}>
                    <DialogTitle>Chi tiết</DialogTitle>
                    <DialogContent>
                        <TextField
                            required
                            margin="dense"
                            name="name"
                            label="Tên thương hiệu"
                            type="text"
                            fullWidth
                            variant="standard"
                            sx={{ marginBottom: 2 }}
                            value={formData.name || ''}
                            onChange={(e) => handleChange(e)}
                        />
                        <Autocomplete
                            multiple
                            id="checkboxes-tags-demo"
                            options={categories}
                            disableCloseOnSelect
                            getOptionLabel={(item: ICategory) => `${item.categoryId} - ${item.categoryName}`}
                            value={categories.length > 0 && formData['categoryIds'] ? categories.filter((item) => formData['categoryIds'].includes(item.categoryId)) : []}
                            renderOption={(props, item, { selected }) => (
                                <li {...props}>
                                    <Checkbox
                                        icon={icon}
                                        checkedIcon={checkedIcon}
                                        style={{ marginRight: 8 }}
                                        checked={selected}
                                        size="small"
                                    />
                                    {item.categoryId} - {item.categoryName}
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField {...params} label="Danh mục" placeholder="Chọn danh mục" size="small" />
                            )}
                            onChange={(event, items) => onChangeCategories(items)}
                            size="small"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={JSON.stringify(formData['isHighlight']) === 'true'}
                                    onChange={onChangeIsHighlight}
                                    name="isHighlight"
                                    color="primary"
                                />}
                            label="Nổi bật"
                        />
                        <Grid item xs={12} sm={12}>
                            <UploadInput
                                id={`imageUser-upload`}
                                ref={uploadRef}
                                multiple
                                hidden
                                onChangeFiles={(event) => onChangeFiles(event)}
                                uploadType="brand"
                                filesLimit={1}
                                isRequired={true}
                                selectedFiles={[formData.imagePath]}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <TextField
                                required
                                margin="dense"
                                name="descriptionTitle"
                                label="Tiêu đề"
                                type="text"
                                fullWidth
                                variant="standard"
                                sx={{ marginBottom: 2 }}
                                value={formData.descriptionTitle || ''}
                                onChange={(e) => handleChange(e)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <Editor
                                initialValue={formData['description'] ?? ''}
                                onInit={(evt, editor) => editorRef.current = editor}
                                apiKey={GlobalConfig.TINY_KEY}
                                init={{
                                    height: 500,
                                    plugins: GlobalConfig.TINY_PLUGINS,
                                    toolbar: GlobalConfig.TINY_TOOLBAR,
                                }}
                            />
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeDialogAndRemoveFiles}>Đóng</Button>
                        <Button type="submit">Xác nhận</Button>
                    </DialogActions>
                </Box>
            )}
        </Fragment>
    )
}

export default BrandList;
